// QR Kiosk v2 — Manuals & software (surface 9). v1 DocumentsPage parity:
// chained getMachine → getDocuments(machine.internalNumber), machine-scoped list.
// Fixes: null-file download disabled-not-crashing + size null-guard (bug #5),
// empty state names the fix-owner, non-403 error state with Retry. Tokens only.
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import Moment from "react-moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { formatFileSize } from "../../../shared/models/models";
import { downloadFile } from "../../../shared/utils/fileUtils";
import { getMachine, getDocuments } from "../../publicSlice";
import { Document } from "../../models/documentsModels";
import { KPage } from "../shared/KPage";
import { Glyph, GD } from "../shared/glyphs";
import { ErrorCallout } from "../shared/ErrorCallout";
import { CachedErrorState } from "../shared/CachedErrorState";
import { useSurfaceState } from "../shared/useSurfaceState";
import { useKioskRouteMachine } from "../shared/useKioskRouteMachine";
import { formatClockTime } from "../shared/clock";
import { EmptyCard } from "../shared/EmptyCard";
import { SkeletonBlock } from "../shared/SkeletonBlocks";
import { usePressed } from "../shared/usePressed";

const cardStyle: React.CSSProperties = {
  margin: "12px 14px 0",
  border: "1px solid var(--border)",
  borderRadius: 12,
  boxShadow: "var(--shadow)",
  background: "var(--card)",
  overflow: "hidden",
  flexShrink: 0,
};

function ManualRow({ doc, index, translate }: { doc: Document; index: number; translate: (k: string) => string }) {
  const press = usePressed();
  const enabled = !!doc.file?.content;

  const segs: React.ReactNode[] = [];
  if (doc.file?.name) segs.push(doc.file.name);
  if (doc.file != null && doc.file.size != null) segs.push(formatFileSize(doc.file.size));
  segs.push(<Moment key="date" format="DD.MM.YYYY, HH:mm" date={doc.createdDate} />);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "13px 14px",
        borderTop: index ? "1px solid var(--border-soft)" : "none",
      }}
    >
      <span
        style={{
          width: 38,
          height: 38,
          borderRadius: 9,
          background: "var(--bg-soft)",
          border: "1px solid var(--border-soft)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--muted)",
          flexShrink: 0,
        }}
      >
        <Glyph d={GD.doc} size={18} />
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {doc.name}
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)", marginTop: 3 }}>
          {segs.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && " · "}
              {s}
            </React.Fragment>
          ))}
        </div>
      </div>

      {enabled ? (
        <button
          type="button"
          {...press.pressHandlers}
          onClick={() => downloadFile(doc.file!.content, doc.file!.name)}
          aria-label={translate("kioskV2Download")}
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            border: "1px solid var(--border)",
            background: "var(--bg)",
            color: "var(--tier-accent)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
            ...press.pressStyle,
          }}
        >
          <Glyph d={GD.down} size={17} />
        </button>
      ) : (
        <button
          type="button"
          disabled
          aria-label={translate("kioskV2DownloadUnavailable")}
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            border: "1px solid var(--border)",
            background: "var(--bg)",
            color: "var(--faint)",
            opacity: 0.45,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "default",
            flexShrink: 0,
          }}
        >
          <Glyph d={GD.down} size={17} />
        </button>
      )}
    </div>
  );
}

function ManualsSkeleton() {
  return (
    <div style={{ ...cardStyle, boxShadow: "none" }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "13px 14px",
            borderTop: i ? "1px solid var(--border-soft)" : "none",
          }}
        >
          <SkeletonBlock w={38} h={38} r={9} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <SkeletonBlock w="55%" h={12} />
            <SkeletonBlock w="70%" h={8} style={{ marginTop: 4 }} />
          </div>
          <SkeletonBlock w={44} h={44} r={10} />
        </div>
      ))}
    </div>
  );
}

export function ManualsV2() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const { documents } = useAppSelector((s) => s.public);

  useEffect(() => {
    if (id) dispatch(getMachine(id));
  }, [id, dispatch]);

  // Route-scoped machine (KV2-1-e) via the shared resolver — resolves + backfills the
  // guest's curated payload so the internalNumber-keyed getDocuments fetch below fires.
  const m = useKioskRouteMachine(id);

  // KV2-5: pattern-B local DocsStatus removed. Document has no machine key, so the
  // documents surface is validated by the slice loadState (loadedForKey) instead.
  const fetchDocs = (n: string) => dispatch(getDocuments(n));
  useEffect(() => {
    if (m?.internalNumber) dispatch(getDocuments(m.internalNumber));
  }, [m?.internalNumber, dispatch]);

  const stMachine = useSurfaceState("machine", id);
  const stDocs = useSurfaceState("documents", m?.internalNumber);
  const docs = documents != null && stDocs.loadedForKey ? documents : undefined;

  const renderNormalBody = (): React.ReactNode =>
    (docs?.length ?? 0) === 0 ? (
      <EmptyCard
        glyph={GD.book}
        title={translate("kioskV2ManualsEmptyTitle")}
        hint={translate("kioskV2ManualsEmptyHint")}
      />
    ) : (
      <div className="kv2-enter" style={cardStyle}>
        {(docs ?? []).map((doc, i) => (
          <ManualRow key={doc.id} doc={doc} index={i} translate={translate} />
        ))}
      </div>
    );

  // §5 tree over machine (context) + documents (body). BUG-1: skeleton covers idle+pending.
  let body: React.ReactNode;
  if (!m) {
    body = stMachine.failed ? (
      <ErrorCallout
        title={translate("kioskV2DashboardLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={() => id && dispatch(getMachine(id))}
        retryLabel={translate("kioskV2Retry")}
      />
    ) : (
      <ManualsSkeleton />
    );
  } else if (!docs) {
    body = stDocs.failed ? (
      <ErrorCallout
        title={translate("kioskV2ManualsLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={() => fetchDocs(m.internalNumber)}
        retryLabel={translate("kioskV2Retry")}
      />
    ) : (
      <ManualsSkeleton />
    );
  } else {
    const machineFailed = stMachine.failed; // outer surface wins the callout
    if (machineFailed || stDocs.failed) {
      body = (
        <CachedErrorState
          title={machineFailed ? translate("kioskV2DashboardLoadFailedTitle") : translate("kioskV2ManualsLoadFailedTitle")}
          hint={translate("kioskV2DashboardLoadFailedHint")}
          onRetry={machineFailed ? () => id && dispatch(getMachine(id)) : () => fetchDocs(m.internalNumber)}
          retryLabel={translate("kioskV2Retry")}
          cachedAt={stDocs.loadedAt}
          cachedLabel={stDocs.loadedAt != null ? translate("kioskV2ShowingCachedFrom", { time: formatClockTime(stDocs.loadedAt) }) : undefined}
        >
          {renderNormalBody()}
        </CachedErrorState>
      );
    } else {
      body = renderNormalBody();
    }
  }

  return (
    <KPage titleKey="manualsAndSoftware" glyph={GD.book} machineNumber={id}>
      {body}
    </KPage>
  );
}

export default ManualsV2;
